import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const EulaPage = () => {
  const { t } = useTranslation();
  
  // Animation variants
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle
          initial="hidden"
          animate="visible"
          variants={fadeIn}
        >
          End-User License Agreement (EULA)
        </PageTitle>
        <PageDate
          initial="hidden"
          animate="visible"
          variants={fadeIn}
          transition={{ delay: 0.1 }}
        >
          {t('legal.lastUpdated')}: April 18, 2025
        </PageDate>
      </PageHeader>

      <ContentContainer
        initial="hidden"
        animate="visible"
        variants={fadeIn}
        transition={{ delay: 0.2 }}
      >
        <IntroSection>
          This End-User License Agreement ("EULA") is a legal agreement between you and Xenya.
        </IntroSection>
        
        <Section>
          <SectionTitle>1. License Grant</SectionTitle>
          <SectionContent>
            Xenya grants you a revocable, non-exclusive, non-transferable, limited license to download, install, and use the Application strictly in accordance with the terms of this Agreement.
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>2. Restrictions</SectionTitle>
          <SectionContent>
            You agree not to, and you will not permit others to:
            <List>
              <ListItem>License, sell, rent, lease, assign, distribute, transmit, host, outsource, disclose or otherwise commercially exploit the Application.</ListItem>
              <ListItem>Copy or use the Application for any purpose other than as permitted under this Agreement.</ListItem>
              <ListItem>Modify, make derivative works of, disassemble, decrypt, reverse compile or reverse engineer any part of the Application.</ListItem>
              <ListItem>Remove, alter or obscure any proprietary notice (including any notice of copyright or trademark) of Xenya or its affiliates, partners, suppliers or licensors.</ListItem>
            </List>
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>3. Intellectual Property</SectionTitle>
          <SectionContent>
            The Application, including without limitation all copyrights, patents, trademarks, trade secrets and other intellectual property rights are, and shall remain, the sole and exclusive property of Xenya.
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>4. Termination</SectionTitle>
          <SectionContent>
            This Agreement shall remain in effect until terminated by you or Xenya. Xenya may, in its sole discretion, at any time and for any or no reason, suspend or terminate this Agreement with or without prior notice.
            <br /><br />
            This Agreement will terminate immediately, without prior notice from Xenya, in the event that you fail to comply with any provision of this Agreement. You may also terminate this Agreement by deleting the Application and all copies thereof from your mobile device or from your computer.
            <br /><br />
            Upon termination of this Agreement, you shall cease all use of the Application and delete all copies of the Application from your mobile device or from your computer.
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>5. Severability</SectionTitle>
          <SectionContent>
            If any provision of this Agreement is held to be unenforceable or invalid, such provision will be changed and interpreted to accomplish the objectives of such provision to the greatest extent possible under applicable law and the remaining provisions will continue in full force and effect.
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>6. Amendments to this Agreement</SectionTitle>
          <SectionContent>
            Xenya reserves the right, at its sole discretion, to modify or replace this Agreement at any time. If a revision is material we will provide at least 30 days' notice prior to any new terms taking effect. What constitutes a material change will be determined at our sole discretion.
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>7. Acceptable Use and Conduct</SectionTitle>
          <SectionContent>
            You agree to use the Application only for purposes that are legal, proper and in accordance with this Agreement and any applicable laws or regulations. You agree not to use the Application in any way that:
            <List>
              <ListItem>Infringes the rights of Xenya or any third party, including without limitation intellectual property rights or rights of privacy.</ListItem>
              <ListItem>Violates any applicable law, statute, ordinance or regulation.</ListItem>
              <ListItem>Is harmful, fraudulent, deceptive, threatening, harassing, defamatory, obscene or otherwise objectionable.</ListItem>
              <ListItem>Involves sending, storing, or making available unsolicited or unauthorized advertising, promotional materials, spam, junk mail, chain letters, pyramid schemes, or any other form of prohibited solicitation.</ListItem>
              <ListItem>Involves any malicious software or malware intended to damage or alter a computer system or data.</ListItem>
            </List>
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>8. User Content</SectionTitle>
          <SectionContent>
            The Application may allow you to create, store, share or publish content, including but not limited to photos, profiles, and facial data ("User Content"). You retain all rights to your User Content. However, by making your User Content available through the Application, you grant to Xenya a non-exclusive, transferable, sublicensable, royalty-free, worldwide license to use, copy, modify, create derivative works based on, distribute, publicly display, publicly perform, and otherwise exploit your User Content in connection with operating and providing the Application.
            <br /><br />
            You represent and warrant that you own or have the necessary rights to your User Content and that the posting and use of your User Content does not violate, misappropriate or infringe on the rights of any third party.
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>9. Privacy</SectionTitle>
          <SectionContent>
            By using the Application, you agree to the collection and use of information in accordance with our Privacy Policy. You consent to our use of facial recognition technology and collection of biometric data strictly for the purpose of providing skin analysis services. We implement reasonable security practices to protect this sensitive data.
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>10. Warranty Disclaimer</SectionTitle>
          <SectionContent>
            THE APPLICATION IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTY OF ANY KIND. XENYA DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            <br /><br />
            XENYA DOES NOT WARRANT THAT THE APPLICATION WILL FUNCTION UNINTERRUPTED, SECURE OR AVAILABLE AT ANY PARTICULAR TIME OR LOCATION, OR THAT ANY ERRORS OR DEFECTS WILL BE CORRECTED.
            <br /><br />
            THE APPLICATION IS NOT INTENDED TO BE USED AS A MEDICAL DEVICE AND IS NOT DESIGNED TO DIAGNOSE, TREAT, CURE OR PREVENT ANY DISEASE OR HEALTH CONDITION. USERS SHOULD ALWAYS CONSULT WITH A QUALIFIED HEALTHCARE PROFESSIONAL REGARDING ANY HEALTH CONCERNS.
          </SectionContent>
        </Section>

        <ContactSection>
          <SectionTitle>Contact Information</SectionTitle>
          <SectionContent>
            If you have any questions about this Agreement, please contact us at:
            <ContactInfo>
              <div>Xenya</div>
              <div>Email: legal@xenya-face.app</div>
              <div>Address: 123 Beauty Lane, Paris, France</div>
            </ContactInfo>
          </SectionContent>
        </ContactSection>
      </ContentContainer>
    </PageContainer>
  );
};

// Styled Components
const PageContainer = styled.div`
  padding-top: 80px;
  min-height: 100vh;
`;

const PageHeader = styled.div`
  text-align: center;
  padding: 60px 20px;
  background: ${({ theme }) => theme.colors.gradientPrimary};
`;

const PageTitle = styled(motion.h1)`
  font-size: ${({ theme }) => theme.fontSizes['4xl']};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  margin-bottom: 10px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fontSizes['3xl']};
  }
`;

const PageDate = styled(motion.p)`
  font-size: ${({ theme }) => theme.fontSizes.md};
  color: ${({ theme }) => theme.colors.gray};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fontSizes.sm};
  }
`;

const ContentContainer = styled(motion.div)`
  max-width: 900px;
  margin: 0 auto;
  padding: 60px 20px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  backdrop-filter: blur(10px);
  margin-top: -30px;
  margin-bottom: 60px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: ${({ theme }) => theme.shadows.lg};
`;

const IntroSection = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.lg};
  line-height: 1.7;
  margin-bottom: 40px;
  color: ${({ theme }) => theme.colors.light};
  opacity: 0.9;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fontSizes.md};
  }
`;

const Section = styled.div`
  margin-bottom: 40px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSizes.xl};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  margin-bottom: 15px;
  color: ${({ theme }) => theme.colors.accent};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fontSizes.lg};
  }
`;

const SectionContent = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.md};
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.light};
  opacity: 0.9;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fontSizes.sm};
  }
`;

const List = styled.ul`
  padding-left: 20px;
  margin: 15px 0;
`;

const ListItem = styled.li`
  margin-bottom: 8px;
  position: relative;
  padding-left: 5px;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  &:before {
    content: '•';
    position: absolute;
    left: -15px;
    color: ${({ theme }) => theme.colors.accent};
  }
`;

const ContactSection = styled(Section)`
  margin-top: 60px;
  padding-top: 30px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const ContactInfo = styled.div`
  background: rgba(255, 255, 255, 0.05);
  padding: 20px;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  margin-top: 15px;
  line-height: 1.8;
`;

export default EulaPage;